import { LinkButton, Tab, TabList, Tabs, Typography } from '@compass/components'
import { protectWithWorkspacePermissions } from '@routes/-protectWithPermission'
import { withCompanyName } from '@routes/-withCompanyName'
import { createFileRoute, Outlet, useLocation } from '@tanstack/react-router'
import { AppRoute } from 'appRoutes'
import { SmartSignalsUpsellBanner } from 'components/SmartSignalsUpsellBanner/SmartSignalsUpsellBanner'
import { DOCS_SUSPECT_SCORE_WEIGHTS } from 'const'
import { MainColumn, SetHeader, smartSignalsNavItems, SubHeader, TabNavigation } from 'features/commonUI'
import { useCurrentSubscription } from 'features/subscription'
import { getDisabledProductNames, SIGNAL_NAME_TO_APP_PRODUCT } from 'helpers/smartSignalMappings'
import { useLDFlag } from 'hooks'
import { useSmartSignalsEnabled } from 'hooks/api/smart_signals'
import { canViewAnalyticsPermission } from 'hooks/usePermission'
import { ExternalLink } from 'lucide-react'
import { ampli } from 'models/ampli'
import { useMemo } from 'react'

export const Route = createFileRoute('/_authenticated/_layout/workspaces/$subscriptionId/suspect-score')({
  component: SuspectScoreWrapperPage,
  head: () => ({
    meta: [
      {
        title: withCompanyName('Suspect Score'),
      },
    ],
  }),
  beforeLoad: protectWithWorkspacePermissions(canViewAnalyticsPermission),
})

const ANALYSIS_ROUTE_SUFFIX = AppRoute.SuspectScoreFraudAnalysis.split('/').slice(-1)[0]
const SMART_SIGNAL_PRODUCTS = [...new Set(Object.values(SIGNAL_NAME_TO_APP_PRODUCT))]

function SuspectScoreWrapperPage() {
  const location = useLocation()
  const showTabs = useLDFlag('FRAUD_LABEL_IMPORT', false)
  const { currentSubscriptionId } = useCurrentSubscription()
  const { data: enabledProducts } = useSmartSignalsEnabled(currentSubscriptionId)

  const disabledSignalNames = useMemo(
    () => (enabledProducts ? getDisabledProductNames(enabledProducts, SMART_SIGNAL_PRODUCTS) : []),
    [enabledProducts]
  )

  return (
    <>
      <SetHeader title='Smart Signals' />
      <TabNavigation items={smartSignalsNavItems} />
      <MainColumn className='relative'>
        {enabledProducts && enabledProducts.length > 0 && disabledSignalNames.length > 0 && (
          <SmartSignalsUpsellBanner
            intro='More Smart Signals are available. Expand your Suspect Score accuracy with signals like'
            disabledSignalNames={disabledSignalNames}
            pagePath={AppRoute.SuspectScore}
          />
        )}
        <SubHeader
          title={
            <Typography variant='heading' size='2xl' as='h2'>
              Suspect Score weights
            </Typography>
          }
          description='Adjust the weight of each Smart Signal that contributes to the Suspect Score.'
          keepRowLayout
          actions={[
            <LinkButton
              key='docs'
              variant='secondary'
              href={DOCS_SUSPECT_SCORE_WEIGHTS}
              target='_blank'
              rel='noopener noreferrer'
              onPress={() => ampli.suspectScoreWeightsDocOpened()}
            >
              Docs <ExternalLink />
            </LinkButton>,
          ]}
        />
        {showTabs ? (
          <Tabs selectedKey={location.pathname.endsWith(`/${ANALYSIS_ROUTE_SUFFIX}`) ? 'analysis' : 'weights'}>
            <TabList>
              <Tab id='weights' href={AppRoute.SuspectScore}>
                Weights
              </Tab>
              <Tab id='analysis' href={AppRoute.SuspectScoreFraudAnalysis} data-testid='tabnav-suspect-score-analysis'>
                Fraud Analysis
              </Tab>
            </TabList>
          </Tabs>
        ) : null}
        <Outlet />
      </MainColumn>
    </>
  )
}
